import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { TranslationKeys } from '../../../../translations';
import { useBreadcrumb } from '../../../../components/Breadcrumbs';
import { ConfirmDialog } from '../../../../components';
import { useSnakeBar } from '../../../../hooks';
import { useStoreSelector } from '../../../../store';
import {
  useOwnerEmployersQuery,
  useDeletedConnectionsQuery,
  useRestoreConnectionMutation,
} from '../../../../hooks/api/admin';
import type { AdminOwnerEmployer, DeletedConnection } from '../../../../types';
import { useIntl } from 'react-intl';
import { EmployersTable } from './components/EmployersTable';
import { ConnectionsTable } from './components/ConnectionsTable';
import { ContainerStyled } from './RestoreConnectionsPage.styles';

export const RestoreConnectionsPage = () => {
  const intl = useIntl();
  const { showSuccessSnakeBar, showErrorSnakeBar } = useSnakeBar();

  useBreadcrumb(TranslationKeys.menu_adminPanel_restoreConnections);

  // Get environment owner
  const environmentOwnerId = useStoreSelector(state => state.AppReducer.environment.ownerId);

  // State
  const [employersSearchQuery, setEmployersSearchQuery] = React.useState('');
  const [selectedEmployer, setSelectedEmployer] = React.useState<AdminOwnerEmployer | null>(null);
  const [connectionToRestore, setConnectionToRestore] = React.useState<DeletedConnection | null>(null);
  const [employersPage, setEmployersPage] = React.useState(1);
  const [employersPerPage, setEmployersPerPage] = React.useState(10);
  const [connectionsPage, setConnectionsPage] = React.useState(1);
  const [connectionsPerPage, setConnectionsPerPage] = React.useState(10);

  const { data: employersData, isLoading: employersLoading } = useOwnerEmployersQuery({
    variables: {
      ownerId: environmentOwnerId,
      page: employersPage,
      itemsPerPage: employersPerPage,
      searchQuery: employersSearchQuery || undefined,
    },
    options: {
      enabled: !!environmentOwnerId,
    },
  });

  const { data: connectionsData, isLoading: connectionsLoading } = useDeletedConnectionsQuery({
    variables: {
      employerId: selectedEmployer?.employerId || '',
      page: connectionsPage,
      itemsPerPage: connectionsPerPage,
    },
    options: {
      enabled: !!selectedEmployer?.employerId,
    },
  });

  const { mutate: restoreConnection, isLoading: restoreLoading } = useRestoreConnectionMutation();

  // Handlers
  const handleEmployersSearchChange = React.useCallback((value: string) => {
    setEmployersSearchQuery(value);
    setEmployersPage(1);
  }, []);

  const handleSelectEmployer = React.useCallback((employer: AdminOwnerEmployer) => {
    setSelectedEmployer(employer);
    setConnectionsPage(1);
  }, []);

  const handleBackToEmployers = React.useCallback(() => {
    setSelectedEmployer(null);
    setConnectionsPage(1);
  }, []);

  const handleRestoreClick = React.useCallback((connection: DeletedConnection) => {
    setConnectionToRestore(connection);
  }, []);

  const handleRestoreConfirm = React.useCallback(() => {
    if (connectionToRestore) {
      restoreConnection(
        { connectionId: connectionToRestore.connectionId },
        {
          onSuccess: () => {
            showSuccessSnakeBar({
              method: 'restoreConnection',
              message: intl.formatMessage({ id: TranslationKeys.tools_restoreConnections_restoreSuccess }),
            });
            setConnectionToRestore(null);
          },
          onError: () => {
            showErrorSnakeBar({
              method: 'restoreConnection',
              message: intl.formatMessage({ id: TranslationKeys.tools_restoreConnections_restoreError }),
            });
          },
        },
      );
    }
  }, [connectionToRestore, intl, restoreConnection, showErrorSnakeBar, showSuccessSnakeBar]);

  const handleRestoreCancel = React.useCallback(() => {
    setConnectionToRestore(null);
  }, []);

  // Employers table
  const employersPagination = React.useMemo(() => {
    return {
      page: employersPage,
      rowsPerPage: employersPerPage,
      totalCount: employersData?.totalItems || 0,
    };
  }, [employersPage, employersPerPage, employersData?.totalItems]);

  const handleEmployersPaginationChange = React.useCallback(
    ({ page, rowSize }: { page?: number; rowSize?: number }) => {
      if (page !== undefined) {
        setEmployersPage(page);
      }
      if (rowSize !== undefined) {
        setEmployersPerPage(rowSize);
      }
    },
    [],
  );

  // Connections table
  const connectionsPagination = React.useMemo(() => {
    return {
      page: connectionsPage,
      rowsPerPage: connectionsPerPage,
      totalCount: connectionsData?.totalItems || 0,
    };
  }, [connectionsPage, connectionsPerPage, connectionsData?.totalItems]);

  const handleConnectionsPaginationChange = React.useCallback(
    ({ page, rowSize }: { page?: number; rowSize?: number }) => {
      if (page !== undefined) {
        setConnectionsPage(page);
      }
      if (rowSize !== undefined) {
        setConnectionsPerPage(rowSize);
      }
    },
    [],
  );

  return (
    <ContainerStyled>
      {/* Employers Section */}
      {!selectedEmployer && (
        <EmployersTable
          data={employersData?.data}
          loading={employersLoading}
          searchQuery={employersSearchQuery}
          onSearchChange={handleEmployersSearchChange}
          onSelectEmployer={handleSelectEmployer}
          pagination={employersPagination}
          onPaginationChange={handleEmployersPaginationChange}
        />
      )}

      {/* Deleted Connections Section */}
      {selectedEmployer && (
        <>
          <Box>
            <Button variant="text" startIcon={<ArrowBack />} onClick={handleBackToEmployers}>
              <FormattedMessage id={TranslationKeys.tools_restoreConnections_backToEmployers} />
            </Button>
          </Box>

          <ConnectionsTable
            data={connectionsData?.data}
            loading={connectionsLoading}
            onRestoreClick={handleRestoreClick}
            restoreLoading={restoreLoading}
            selectedEmployerName={selectedEmployer.employerName}
            pagination={connectionsPagination}
            onPaginationChange={handleConnectionsPaginationChange}
          />
        </>
      )}

      {/* Restore Confirmation Dialog */}
      <ConfirmDialog
        open={!!connectionToRestore}
        title={<FormattedMessage id={TranslationKeys.tools_restoreConnections_restoreConfirmTitle} />}
        onConfirm={handleRestoreConfirm}
        onClose={handleRestoreCancel}
      >
        <FormattedMessage
          id={TranslationKeys.tools_restoreConnections_restoreConfirmMessage}
          values={{ connectionName: connectionToRestore?.connectionName || '' }}
        />
      </ConfirmDialog>
    </ContainerStyled>
  );
};
